<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { tabFocus, getActualKey, longPressAction, orderableChildren } from '@oat-sa-private/ui-core';
    import TextCell from './TextCell.svelte';
    import TableHead from './TableHead.svelte';
    import DragButtonCell from './DragButtonCell.svelte';

    export let headComponent;
    export let columns = [];
    export let rows = [];
    export let fullwidth = false;
    export let altrows = false;
    export let currentRow = -1;
    export let allSelected = false;
    export let rowKey = (row, rowIndex) => rowIndex;
    export let rowsConfig = {};
    // If we add click listeners to the table rows, screen readers announce them as clickable.
    export let clickableRows = false;
    export let rowDisabled = () => false;
    export let rowSelected = () => false;
    export let orderable = false;
    export let liveInstructions = '';

    const dispatch = createEventDispatcher();

    let focusesFromKey = new Array(rows.length); // class toggler instead of :focus-visible to prevent conflict with native

    $: tabindex = clickableRows ? '0' : void 0;

    //ordering support constants, variables
    const dragDelay = 100;
    const dragSymbol = Symbol('drag');
    let tableElement;

    //adding draggable icon button
    $: if (orderable) {
        if (columns && columns.length) {
            if (columns[0].dragSymbol !== dragSymbol) {
                columns = [
                    {
                        key: 'drag',
                        title: '',
                        cellComponent: DragButtonCell,
                        dragSymbol
                    },
                    ...columns
                ];
            }
        }
        if (rows && rows.length) {
            rows = rows.map((row, index) => {
                if (row[0].dragSymbol === dragSymbol) {
                    row[0] = { dragSymbol, index, columnKey: 'drag' };
                } else {
                    row = [{ dragSymbol, index, columnKey: 'drag' }, ...row];
                }
                return row;
            });
        }
    }

    // soft validation of columns vs rows
    // because it's too easy to dynamically prepare column & row data with a mismatch
    $: if (columns.length && rows.length && rows[0].length) {
        for (let i = 0; i < rows[0].length; i++) {
            const rowColKey = rows[0][i]?.columnKey;
            const colKey = columns[i]?.key;
            if (rowColKey && colKey && (rowColKey !== colKey)) {
                // eslint-disable-next-line
                console.log(`Warning! Datatable rows[0][${i}].columnKey ('${rowColKey}') did not match columns[${i}].key ('${colKey}')`);
            }
        }
    }

    /**
     * Keypress method checks if KB key is either SPACE or ENTER
     * preventDefault() prevents space's default behavior that is scrolling down
     * when the correct key is pressed, the click action method is invoked
     * @param {KeyboardEvent} e keyboard key event
     * @param {Number} row - rows list
     * @param {Number} rowIndex - index of row in rows list
     */
    function checkIfAllowedKey(e, row, rowIndex) {
        const key = getActualKey(e);
        if (key === 'enter' || key === 'space') {
            e.preventDefault();
            action('rowClick', rowKey(row, rowIndex), row);
        }
    }

    /**
     * @param {String} type - event type
     * @param {Object|String} key - row key
     * @param {Object} resource - row object
     * @param {Object} options - additional event options
     * @fires action
     * @fires rowClick
     * @fires rowLongClick
     */
    function action(type, key, resource, options) {
        if (type === 'keyboardorder') {
            const keyboardorderEvent = new CustomEvent('keyboardorder', { detail: { index: options.index } });
            tableElement.dispatchEvent(keyboardorderEvent);
        }
        dispatch(
            'action',
            Object.assign(
                {
                    type,
                    key,
                    resource
                },
                options
            )
        );
        dispatch(
            type,
            Object.assign(
                {
                    key,
                    resource
                },
                options
            )
        );
    }

    /**
     * Finds row in config and returns passed property.
     *
     * @param {Number} rowIndex - index of row in rows list
     * @param {String} property - type of row to check
     *
     * @returns {*} - returns the value if row and property exist, undefined otherwise.
     */
    function getRowProperty(rowIndex, property) {
        const index = rowKey(rows, rowIndex);

        return rowsConfig[index] && rowsConfig[index][property];
    }

    /**
     * Determines if row is warning or not.
     *
     * @param {Number} rowIndex - index of row in rows list
     *
     * @returns {Boolean}
     */
    function isRowWarning(rowIndex) {
        return !!getRowProperty(rowIndex, 'warning');
    }


    /**
     * Table viewport params (used by inner menu for calculating boundaries overlapping)
     * @returns {{top: number, bottom: number, x: number, width: number, y: number, height: number}}
     */
    function getBoundingParentRect() {
        const tableParent = tableElement.parentElement;
        const { x, y, top, bottom, height, width } = tableParent.getBoundingClientRect();
        const tableHead = tableElement.querySelector('thead');
        const { height: headHeight } = tableHead.getBoundingClientRect();
        return {
            x,
            y: y + headHeight,
            top,
            bottom,
            height: height - headHeight,
            width,
        };
    }

    $: someSelected = rows.some(rowSelected);
    $: allRowsSelected = rows.length && rows.every(rowSelected) || allSelected;
</script>

<style>
    table {
        --color-border-accent: var(--color-brand);

        & tr {
            border-inline-start: var(--space-half) solid transparent;
            border-inline-end: none;
            border-bottom: var(--border-thin) solid var(--color-border-info);

            &:last-child {
                border-bottom: none;
            }

            &:hover,
            &.selected:hover {
                background-color: var(--color-bg-actionable-secondary-hover);
            }

            &:focus,
            &:active {
                outline: none;
            }

            &.selected {
                background-color: var(--color-bg-selection);
            }

            &.focusable.key-focus-visible {
                background-color: var(--color-bg-actionable-secondary-hover);
                @add-mixin simple-outline var(--color-border-focus), 0;
            }

            &.focusable {
                &:hover {
                    cursor: pointer;
                }
            }

            &.disabled {
                &:hover {
                    cursor: not-allowed;
                }
            }

            &.warning {
                color: var(--color-bg-warning);
            }
        }
    }

    /** check orderableChildren svelte action to understand purpose of these styles */
    :global(table.orderable-children-ordering) {
        & tr {
            user-select: none;
            &:hover {
                border-bottom: var(--border-medium) solid var(--color-brand);
                &:not(.orderable-children-selected) {
                    background-color: unset;
                }
            }
        }
    }
    :global(table.orderable-children-ordering tr.orderable-children-selected) {
        background-color: var(--color-bg-selection);
        &:hover {
            background-color: var(--color-bg-selection);
        }
    }

    :global(table.orderable-children-ordering tr.orderable-children-placement) {
        border-bottom: var(--border-medium) solid var(--color-brand);
    }

    :global(table.orderable-children-ordering tr.orderable-children-placement:last-child) {
        border-bottom: var(--border-medium) solid var(--color-brand);
    }

    :global(table.orderable-children-ordering-keyboard) {
        & tr:not(.orderable-children-placement):hover {
            border-bottom: var(--border-thin) solid var(--color-border-info);
        }
    }
    /** end orderableChildren svelte action styles */

    .fullwidth {
        width: 100%;
    }

    .altrows {
        & tr {
            border-bottom: none;

            &:nth-child(even) {
                background-color: var(--color-bg-info);
            }

            &:hover {
                background-color: var(--color-bg-actionable-secondary-hover);
            }

            &.focusable {
                &:hover {
                    cursor: pointer;
                }
            }
        }
    }

    .currentrow {
        border-inline-start: var(--space-half) solid var(--color-border-accent);
        border-inline-end: none;
    }

    .visually-hidden {
        /* if table has scrollbar, live instruction causes extra scrollbar to appear */
        top: 0;
        left: 0;
    }
</style>

<table
    class:fullwidth
    class:altrows
    bind:this={tableElement}
    use:orderableChildren={{ enabled: orderable, delay: dragDelay, selector: `tbody tr` }}
    on:order={event => action('order', null, null, { ...event.detail })}
    on:placement={event => action('placement', null, null, { ...event.detail })}
    on:ordercancelled={() => action('ordercancelled', null, null, null)}>
    <svelte:component
        this={headComponent || TableHead}
        {columns}
        bind:rows
        allSelected={allRowsSelected}
        {someSelected}
        on:action={({ detail: { type, key, resource, options } }) => action(type, key, resource, options)} />
    <tbody>
    {#each rows as row, rowIndex (rowKey(row, rowIndex))}
        <tr
            {tabindex}
            use:longPressAction={{ subscribe: clickableRows }}
            on:keydown|self={e => checkIfAllowedKey(e, row, rowIndex)}
            on:press={() => action('rowClick', rowKey(row, rowIndex), row)}
            on:longpress={() => action('rowLongClick', rowKey(row, rowIndex), row)}
            class:currentrow={rowIndex === currentRow}
            class:focusable={clickableRows && !rowDisabled(row, rowIndex)}
            class:disabled={rowDisabled(row, rowIndex)}
            class:selected={rowSelected(row, rowIndex)}
            use:tabFocus={clickableRows}
            on:tabfocus={() => (focusesFromKey[rowIndex] = true)}
            on:blur={() => (focusesFromKey[rowIndex] = false)}
            class:warning={isRowWarning(rowIndex)}
            class:key-focus-visible={focusesFromKey[rowIndex]}>
            {#each row as cell, cellNumber}
                <td disabled={cell && cell.disabled ? true : void 0}>
                    {#if cell !== null && columns[cellNumber]}
                        <svelte:component
                            this={columns[cellNumber].cellComponent || TextCell}
                            bind:data={cell}
                            on:action={
                                ({ detail: { type, options } }) => action(type, rowKey(row, rowIndex), row, options)
                            }
                            on:change={event => {
                                const isOpen = event.detail;
                                if (isOpen) {
                                    // Close all other menus
                                    focusesFromKey = focusesFromKey.map((value, index) => index === rowIndex);
                                } else {
                                    focusesFromKey[rowIndex] = false;
                                }
                            }}
                            bind:isOpen={focusesFromKey[rowIndex]}
                            getBoundingParentRect={getBoundingParentRect}
                        />
                    {/if}
                </td>
            {/each}
        </tr>
    {/each}
    </tbody>
</table>
<div class="visually-hidden" aria-live="polite">
    {liveInstructions}
</div>
